import { PULSE_ITEM_TYPE, PULSE_ACTION_TYPE } from '@/modules/pulse/utils/pulseConstants';

export const pulseItemName = (item) => item?.context?.context_name || item?.actions?.[0]?.payload?.client_name || null;

export const pulseItemTitle = (item) => item?.display?.title || null;

export const pulseItemType = (item) => {
  const action = item?.actions?.[0]?.action;
  if (!action) return null;
  return action === PULSE_ACTION_TYPE.ACKNOWLEDGE
    ? PULSE_ITEM_TYPE.TAKE_A_LOOK
    : PULSE_ITEM_TYPE.ACTION_READY;
};

// Whether the item carries something to send — which decides the primary CTA (send vs
// open the entity page) for both `pulseItemHoverActions` and `PulseFeedView.onCardAction`.
// An item with no action at all has nothing to send and no `actions[0].uid` to resolve, so
// it reads as "take a look" rather than being offered a Send that could only fail.
export const pulseItemHasArtifact = (item) => {
  const action = item?.actions?.[0];
  if (!action) return false;
  const explicit = action.payload?.artifact ?? item?.display?.artifact;
  if (typeof explicit === 'boolean') return explicit;
  return (action.action || null) !== PULSE_ACTION_TYPE.ACKNOWLEDGE;
};

const CONTEXT_ROUTE = {
  client: 'app.client',
  appointment: 'app.appointment.show',
  payment: 'app.payment.show',
  conversation: 'app.conversation.show',
  document: 'app.document.show',
  estimate: 'app.estimate.show',
};

export const pulseItemNavigation = (item) => {
  const type = item?.context?.context_type;
  const uid = item?.context?.context_uid;
  const route = CONTEXT_ROUTE[type];
  if (!route || !uid) return null;
  return { route, queryParams: { uid } };
};

// Leading term of the Conversita topic title. Takes a category, not an item — the caller
// picks the stored category or the display bucket, which differ. Null when unrecognised.
// See the recommendation README, "Topic title".
const CATEGORY_LABEL_KEY = {
  needs_attention: 'pulse.categories.needs_attention',
  opportunity: 'pulse.categories.opportunity',
  upcoming: 'pulse.categories.upcoming',
  fyi: 'pulse.categories.fyi',
};

export const pulseCategoryLabelKey = (category) => CATEGORY_LABEL_KEY[category] || null;

// The primary CTA's text: the backend's `btn_text` and only that — never derived from the
// action type (see the recommendation README, "CTA label").
export const pulseItemCtaLabel = (item) => {
  const btnText = item?.actions?.[0]?.display?.btn_text;
  return typeof btnText === 'string' && btnText.trim() ? btnText.trim() : null;
};

const MIRRORED_ICON = {
  'icon-Chevron-right': 'icon-Chevron-left',
  'icon-Chevron-left': 'icon-Chevron-right',
};

export const resolveDirectionalIcon = (icon, isRtl) => (isRtl && MIRRORED_ICON[icon]) || icon;

const DISMISS = {
  key: 'not_relevant', event: 'dismiss', icon: 'icon-Trash1', labelKey: 'pulse.item.not_relevant',
};
// icon-check reads ~1.65x smaller than icon-Trash1 at the same size; scaled up to match.
const MARK_DONE = {
  key: 'mark_done', event: 'complete', icon: 'icon-check', iconSize: '26', labelKey: 'pulse.item.mark_done',
};
const VIEW = {
  key: 'open_in_chat', event: 'open-chat', icon: 'icon-Chevron-right', labelKey: 'pulse.item.open_in_chat',
};
const SEND = {
  key: 'send',
  event: 'primary',
  labelFromCta: true,
  labelKey: 'pulse.item.action_cta.fallback',
};

// Returns { send, actions, primary } — see pulse/README.md, "The hover row".
// `send` renders as its own primary CTA, separate from the ghost dismiss actions.
export const pulseItemHoverActions = (item) => ({
  send: pulseItemHasArtifact(item) ? SEND : null,
  actions: [MARK_DONE, DISMISS],
  primary: VIEW,
});
